import {
  Box,
  Image,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Flex,
} from "@chakra-ui/react"
import type { SlideGeneric } from "@types"

interface ReviewSlideProps {
  slide: SlideGeneric
  groupName: string
  deckName: string
}

export function ReviewSlide({ slide }: ReviewSlideProps) {
  return (
    <Card
      borderRadius="xl"
      boxShadow="md"
      bg="gray.50"
      borderWidth="1px"
      borderColor="gray.200"
      overflow="hidden"
    >
      <CardHeader bg="white" borderBottom="1px" borderColor="gray.200" py={3}>
        <HStack justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="bold" color="gray.800">
            {slide.title.en}
          </Text>
          <Badge
            colorScheme={slide.type === "event" ? "purple" : "blue"}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            {slide.type}
          </Badge>
        </HStack>
      </CardHeader>

      <CardBody p={5}>
        <VStack align="stretch" spacing={5}>
          {/* Description and Image Row */}
          <Grid
            templateColumns={{
              base: "1fr",
              md: slide.image ? "1fr 300px" : "1fr",
            }}
            gap={5}
          >
            <GridItem>
              <Text color="gray.700" fontSize="md" lineHeight="tall">
                {slide.description.en}
              </Text>
            </GridItem>

            {slide.image && (
              <GridItem>
                <Box
                  borderRadius="lg"
                  overflow="hidden"
                  boxShadow="sm"
                  bg="linear-gradient(135deg, #fef3c7 0%, #fbbf24 50%, #f59e0b 100%)"
                >
                  <Image
                    src={slide.image}
                    srcSet={slide.imageSrcSet}
                    sizes="300px"
                    loading="lazy"
                    decoding="async"
                    alt={slide.imageAlt}
                    objectFit="contain"
                    w="100%"
                    h="200px"
                  />
                </Box>
              </GridItem>
            )}
          </Grid>

          <Divider borderColor="gray.300" />

          {/* Choices Section */}
          <Box>
            <Text fontWeight="bold" mb={3} color="gray.700" fontSize="md">
              Available Choices:
            </Text>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={3}
            >
              {slide.selections.map((selection, index) => (
                <GridItem key={index}>
                  <Card
                    variant="outline"
                    borderRadius="lg"
                    bg="white"
                    borderColor="purple.200"
                    boxShadow="sm"
                    h="100%"
                  >
                    <CardBody p={4}>
                      <Text
                        fontWeight="semibold"
                        color="gray.800"
                        mb={2}
                        fontSize="sm"
                      >
                        {selection.title.en}
                      </Text>
                      {selection.effects && (
                        <Box>
                          <Text
                            fontSize="xs"
                            color="gray.600"
                            mb={2}
                            fontWeight="medium"
                          >
                            Effects:
                          </Text>
                          <Flex wrap="wrap" gap={1}>
                            {Object.entries(selection.effects).map(
                              ([key, value]) => {
                                if (
                                  key === "tags" &&
                                  typeof value === "object"
                                ) {
                                  return (
                                    <Box key={key}>
                                      {value.add &&
                                        value.add.map((tag: string) => (
                                          <Badge
                                            key={tag}
                                            colorScheme="green"
                                            mr={1}
                                            mb={1}
                                            fontSize="xs"
                                          >
                                            +{tag}
                                          </Badge>
                                        ))}
                                      {value.remove &&
                                        value.remove.map((tag: string) => (
                                          <Badge
                                            key={tag}
                                            colorScheme="red"
                                            mr={1}
                                            mb={1}
                                            fontSize="xs"
                                          >
                                            -{tag}
                                          </Badge>
                                        ))}
                                    </Box>
                                  )
                                }
                                const numValue =
                                  typeof value === "number" ? value : 0
                                return (
                                  <Badge
                                    key={key}
                                    colorScheme={
                                      numValue > 0
                                        ? "green"
                                        : numValue < 0
                                          ? "red"
                                          : "gray"
                                    }
                                    fontSize="xs"
                                  >
                                    {key}: {numValue > 0 ? "+" : ""}
                                    {String(value)}
                                  </Badge>
                                )
                              },
                            )}
                          </Flex>
                        </Box>
                      )}
                    </CardBody>
                  </Card>
                </GridItem>
              ))}
            </Grid>
          </Box>

          {/* Requirements Section */}
          {slide.requirements && Object.keys(slide.requirements).length > 0 && (
            <>
              <Divider borderColor="gray.300" />
              <Box>
                <Text fontWeight="bold" mb={2} color="gray.700" fontSize="md">
                  Requirements:
                </Text>
                <Flex wrap="wrap" gap={2}>
                  {Object.entries(slide.requirements).map(([key, value]) => (
                    <Badge
                      key={key}
                      colorScheme="orange"
                      fontSize="xs"
                      px={2}
                      py={1}
                    >
                      {key}: {JSON.stringify(value)}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  )
}
